import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import Images from "../../utils/Images";
import { useNavigate } from "react-router-dom";
  
export default function GuestNavbar() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Contact", href: "/contact" },
  ];

  const dropdownItems = [
    { name: "Life Insurance", href: "/life-insurance" },
    { name: "Health Insurance", href: "/health-insurance" },
    { name: "Auto Insurance", href: "/auto-insurance" },
    { name: "Travel Insurance", href: "/travel-insurance" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "border-b border-gray-200 shadow-lg backdrop-blur-md bg-white/95"
          : "backdrop-blur-md bg-gray-900/95"
      }`}
    >
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center space-x-2"
          >
            {
              isScrolled ? (
                <img src={Images.logo_colored} className="w-48" alt="TripSecure" />
              ) : (
                <img src={Images.logo_navbar} className="w-full" alt="TripSecure" />
              )
            }
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden items-center space-x-8 lg:flex">
            {navLinks.map((link, index) => (
              <motion.a
                key={link.name}
                href={link.href}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`text-sm font-medium transition-colors hover:text-emerald-600 ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                {link.name}
              </motion.a>
            ))}

            {/* Services Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="relative"
            >
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className={`flex items-center space-x-1 text-sm font-medium transition-colors hover:text-emerald-600 ${
                  isScrolled ? "text-gray-700" : "text-white"
                }`}
              >
                <span>Services</span>
                <ChevronDown
                  size={16}
                  className={`transition-transform ${
                    isDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  className="absolute left-0 top-full py-2 mt-2 w-48 bg-white rounded-lg border border-gray-200 shadow-lg"
                >
                  {dropdownItems.map((item) => (
                    <a
                      key={item.name}
                      href={item.href}
                      className="block px-4 py-2 text-sm text-gray-700 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                    >
                      {item.name}
                    </a>
                  ))}
                </motion.div>
              )}
            </motion.div>
          </div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="hidden lg:block"
          >
            <button onClick={()=>navigate("/signin")} className="px-6 py-2 font-medium text-white bg-emerald-600 rounded-full shadow-lg transition-all duration-300 hover:bg-emerald-700 hover:scale-105">
              Register As Agent
            </button>
          </motion.div>

          {/* Mobile Menu Button */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md ${
              isScrolled ? "text-gray-700" : "text-white"
            }`}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-white border-t border-gray-200 lg:hidden"
        >
          <div className="px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="block text-base font-medium text-gray-700 transition-colors hover:text-emerald-600"
              >
                {link.name}
              </a>
            ))}

            {/* Mobile Services */}
            <div className="space-y-2">
              <p className="text-sm font-medium tracking-wider text-gray-500 uppercase">
                Services
              </p>
              {dropdownItems.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="block pl-4 text-base text-gray-700 transition-colors hover:text-emerald-600"
                >
                  {item.name}
                </a>
              ))}
            </div>

            {/* Mobile CTA */}
            <div className="pt-4">
              <button className="px-6 py-3 w-full font-medium text-white bg-emerald-600 rounded-full transition-all duration-300 hover:bg-emerald-700">
                Register As Agent
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </nav>
  );
}
